import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { AuthUser } from '../../interfaces/Contexts';
import JobCardss from '../../Components/JobCards';
import Filters from './Filters';
import { getJobs } from '../../Utils/apiService';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import KenColors from '../../Helpers/Themes/kenColors';
import { useStyles } from './styles'; // or '../../styles' if needed

const JobsViewAll: React.FC = () => {
  const {
    state: { user },
  } = useContext(AuthContext);
  const userData = user as AuthUser;

  const [filter, setFilter] = useState(false);
  const [search, setSearch] = useState('');
  const [allJobsData, setAllJobsData] = useState([]);
  const [bookmarksData, setBookmarksData] = useState([]);
  const [selectedValues, setSelectedValues] = useState<any>({});
  const [jobFilterData, setJobFilterData] = useState({});
  const [originalJobsData, setOriginalJobsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingSelectedValues, setPendingSelectedValues] = useState<any>({});

  const classes = useStyles();

  useEffect(() => {
    getJobs(userData.applicantId).then((res) => {
      setOriginalJobsData(res.data.allJobs);
      setAllJobsData(res.data.allJobs);
      setJobFilterData(res.data); // Make sure to set filter data for Filters component
    });
  }, []);

  const handleFilterOpen = () => {
    setPendingSelectedValues(selectedValues); // Copy current filters to pending
    setFilter(true);
  };

  const handleFilterCancellation = () => setFilter(false);

  const handleFilterApply = (newValues: any) => {
    setSelectedValues(newValues);
    setFilter(false);
  };

  const handleChipDelete = (key: string, value: string) => {
    setSelectedValues((prev: any) => {
      const updated = { ...prev };
      if (Array.isArray(updated[key])) {
        updated[key] = updated[key].filter((v: string) => v !== value);
        // Remove key if array is empty
        if (updated[key].length === 0) delete updated[key];
      } else {
        delete updated[key];
      }
      // Special handling for Salary
      if (key === 'Salary') {
        delete updated['Salary_Start_Range__c'];
        delete updated['Salary_End_Range__c'];
      }
      return updated;
    });
  };

  // Filtering logic: search + filter
  const getFilteredJobs = () => {
    let filtered = [...originalJobsData];

    // Apply filter values
    Object.entries(selectedValues).forEach(([key, value]: any) => {
      if (!value || value.length === 0) return;
      if (key === 'Salary') return; // Salary handled by Salary_Start_Range__c/Salary_End_Range__c
      if (key === 'Organization__r.Name') {
        filtered = filtered.filter((job: any) =>
          value.includes(job.Organization__r?.Name)
        );
      } else if (key === 'Work_Place_Type__c') {
        filtered = filtered.filter((job: any) =>
          job.Work_Place_Type__c && value.some((v: string) => job.Work_Place_Type__c.includes(v))
        );
      } else if (key === 'Posted_by') {
        if (value.includes('myself')) {
          filtered = filtered.filter((job: any) => job.PostedById === userData.applicantId);
        }
        // 'Anyone' means no filter
      } else if (key === 'Location__c') {
        filtered = filtered.filter((job: any) =>
          value.includes(job.Location__c)
        );
      } else if (key === 'Job_Type__c') {
        filtered = filtered.filter((job: any) =>
          value.includes(job.Job_Type__c)
        );
      } else if (key === 'Skill_Required__c') {
        filtered = filtered.filter((job: any) =>
          job.Skill_Required__c && value.some((v: string) => job.Skill_Required__c.includes(v))
        );
      } else if (key === 'Job_Title__c') {
        filtered = filtered.filter((job: any) =>
          value.includes(job.Job_Title__c)
        );
      }
      // Add more keys as needed
    });

    // Salary filter
    if (
      selectedValues.Salary_Start_Range__c &&
      selectedValues.Salary_End_Range__c
    ) {
      filtered = filtered.filter((job: any) => {
        const min = Math.min(...selectedValues.Salary_Start_Range__c);
        const max = Math.max(...selectedValues.Salary_End_Range__c);
        return (
          job.Salary_Start_Range__c >= min &&
          job.Salary_End_Range__c <= max
        );
      });
    }

    // Search filter
    if (search) {
      filtered = filtered.filter((job: any) =>
        job.Job_Title__c?.toLowerCase().includes(search.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredJobs = getFilteredJobs();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  return (
    <Grid style={{ padding: '10px', backgroundColor: 'white', height: '83.8vh', borderRadius: '10px', overflow: 'scroll' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Grid sx={{ p: isMobile ? 1 : 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search job title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: isMobile ? '70%' : '30%' }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleFilterOpen}
              startIcon={
                <FilterAltOutlinedIcon
                  sx={{
                    fontSize: '14px',
                    color: KenColors.ClientBlack2,
                    '@media (min-width: 1280px) and (max-width: 1300px)': {
                      fontSize: '12px',
                    },
                  }}
                />
              }
              className={classes.filterBtn}
              sx={{
                textTransform: 'capitalize',
                height: '36px',
                backgroundColor: '#FEF6E6',
                '@media (min-width: 1280px) and (max-width: 1300px)': {
                  height: '30px',
                },
                ml: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  '@media (min-width: 1280px) and (max-width: 1300px)': {
                    fontSize: '10px',
                  },
                }}
              >
                Filters
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {/* Filter Chips - moved here, above job cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {Object.entries(selectedValues).map(([key, values]: [string, any]) => {
          if (!values || key.endsWith('_Range__c')) return null; // Skip salary range keys
          if (Array.isArray(values)) {
            return values.map((val: string) => (
              <Chip
                key={key + val}
                label={`${key === 'Organization__r.Name' ? 'Company' : key.replace(/__/g, '').replace(/_/g, ' ')}: ${val}`}
                onDelete={() => handleChipDelete(key, val)}
                sx={{ fontSize: '12px', m: 0.5 }}
              />
            ));
          }
          return (
            <Chip
              key={key + values}
              label={`${key}: ${values}`}
              onDelete={() => handleChipDelete(key, values)}
              sx={{ fontSize: '12px', m: 0.5 }}
            />
          );
        })}
      </Box>
      {/* End Filter Chips */}
      <Grid container spacing={isMobile ? 2 : 4} sx={{ mt: 1, width: '100%' }}>
        {filteredJobs.map((val: any, index: number) => (
          <Grid item xs={6} sm={12} md={12} xl={3} lg={3} key={val.Id}>
            <JobCardss
              value={val}
              userData={userData}
              isBookmarked={bookmarksData.some((job: any) => job.Id === val.Id)}
              onBookmarkClick={() => console.log('Bookmark clicked')}
              setIsBookmarked={() => console.log('Set bookmark')}
            />
          </Grid>
        ))}
      </Grid>
      {/* Filter Modal */}
      <Modal open={filter} onClose={handleFilterCancellation} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description" BackdropProps={{
        invisible: true,
      }}>
        <Filters
          handleClose={handleFilterCancellation}
          jobFilterData={jobFilterData}
          selectedValues={pendingSelectedValues}
          setSelectedValues={setPendingSelectedValues}
          setLoading={setLoading}
          handleDropDownChange={handleFilterApply}
          modalLeft={{ xs: '50%', sm: '50%', md: '86%', lg: '86%', xl: '86%' }}
          fromJobsViewAll={true}
        />
      </Modal>
    </Grid>
  );
};

export default JobsViewAll;